import React from 'react';
import { ActionItem } from './types';
import { 
  getPriorityColor, 
  getStatusColor, 
  getPriorityIcon, 
  getStatusIcon, 
  formatTaskStatus, 
  formatTaskPriority 
} from './utils';

interface TaskCardProps {
  task: ActionItem;
  onQuickComplete: (taskId: string) => void;
  onEdit: (task: ActionItem) => void;
  onViewTranscript: (transcriptId: string) => void;
  onDelete: (taskId: string) => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ 
  task, 
  onQuickComplete, 
  onEdit, 
  onViewTranscript, 
  onDelete 
}) => {
  return (
    <div className="p-6 hover:bg-gray-50 transition-colors">
      {/* Client Names - Show above task title if they exist */}
      {task.client_names && task.client_names.length > 0 && (
        <div className="mb-3">
          <div className="flex flex-wrap gap-2">
            {task.client_names.map((clientName, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
              >
                👤 {clientName}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Task Title and Transcript Info */}
      <div className="mb-4">
        <div className="flex items-start justify-between mb-2">
          <h4 className="text-lg font-semibold text-gray-900 flex-1">{task.title}</h4>
          <div className="ml-4 text-sm text-gray-500">
            From: <span className="font-medium">{task.transcript_title}</span>
          </div>
        </div>
      </div>

      {/* AI Reasoning */}
      {task.ai_reasoning && task.created_by === 'ai' && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <span className="text-purple-600 text-sm font-medium">🧠 AI Context:</span>
            <p className="text-purple-700 text-sm leading-relaxed">{task.ai_reasoning}</p>
          </div>
        </div>
      )}

      {/* Metadata Row */}
      <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
        <div className="flex items-center gap-6">
          {task.category && (
            <div className="flex items-center gap-1">
              <span className="font-medium">Category:</span>
              <span className="px-2 py-1 bg-gray-200 rounded text-gray-700">{task.category}</span>
            </div>
          )}
          {task.assignee && (
            <div className="flex items-center gap-1">
              <span className="font-medium">Assignee:</span>
              <span>{task.assignee}</span>
            </div>
          )}
          {task.due_date && (
            <div className="flex items-center gap-1">
              <span className="font-medium">Due:</span>
              <span>{new Date(task.due_date).toLocaleDateString()}</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2 text-gray-400">
          <span>Created {new Date(task.created_at).toLocaleDateString()}</span>
          {task.created_by === 'ai' ? (
            <span 
              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200" 
              title={`AI Confidence: ${Math.round((task.ai_confidence || 0) * 100)}%`}
            >
              🤖 AI
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
              👤 User
            </span>
          )}
        </div>
      </div>

      {/* Notes */}
      {task.notes && (
        <div className="mb-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <span className="text-yellow-600 text-sm font-medium">📝 Notes:</span>
              <p className="text-yellow-800 text-sm leading-relaxed">{task.notes}</p>
            </div>
          </div>
        </div>
      )}

      {/* Action Bar */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-200">
        {/* Priority and Status Badges */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Priority:</span>
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${getPriorityColor(task.priority)}`}>
              {getPriorityIcon(task.priority)} {formatTaskPriority(task.priority)}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Status:</span>
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(task.status)}`}>
              {getStatusIcon(task.status)} {formatTaskStatus(task.status)}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {task.status !== 'completed' && (
            <button
              onClick={() => onQuickComplete(task.id)}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
              title="Mark as completed"
            >
              ✓ Complete
            </button>
          )}
          <button
            onClick={() => onEdit(task)}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
          >
            ✏️ Edit
          </button>
          <button
            onClick={() => onViewTranscript(task.transcript_id)}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
          >
            📄 View Transcript
          </button>
          <button
            onClick={() => onDelete(task.id)}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
          >
            🗑️ Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaskCard; 